import { useEffect, useState } from "react";
import api, { apiError, pctIt } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Save, Send } from "lucide-react";
import { Motivazione, Allegati, TIPOLOGIE } from "@/components/RichiestaCampi";

export const parseEur = (s) => {
  const t = String(s ?? "").replace(/[€\s]/g, "").replace(/\./g, "").replace(",", ".");
  const n = parseFloat(t);
  return Number.isFinite(n) && n > 0 ? n : null;
};
const fmt = (n, dec = 2) => Number(n).toLocaleString("it-IT", { minimumFractionDigits: dec, maximumFractionDigits: dec });
export const valutatoK = (v) => (v?.min ? `Valutato: ${Math.round(v.min / 1000)}–${Math.round(v.max / 1000)} k€` : null);
const pct = (a, b) => (a && b ? Math.round(((a - b) / b) * 100) : null);
const sgn = (p) => (p > 0 ? `+${pctIt(p)}` : pctIt(p));

function Campo({ label, value, onChange, testid }) {
  const n = parseEur(value);
  return (
    <label className="block">
      <span className="text-[11px] font-bold uppercase tracking-wide text-[var(--muted)]">{label}</span>
      <input data-testid={testid} inputMode="decimal" value={value} onChange={(e) => onChange(e.target.value)} placeholder="es. 250.000"
        className="w-full mt-1 px-3 py-2.5 rounded-xl border border-[var(--border)] text-base sm:text-sm bg-white mono" />
      <span data-testid={`${testid}-anteprima`} className="block text-[11px] text-slate-500 mt-0.5 min-h-[16px]">{n ? `= ${fmt(n)} €` : ""}</span>
    </label>
  );
}

function Confronto({ min, max, mq, omi }) {
  if (!min || !max) return null;
  const pMin = pct(mq ? min / mq : null, omi?.compr_min), pMax = pct(mq ? max / mq : null, omi?.compr_max);
  return (
    <div className="p-3 rounded-xl bg-[#FC82ED]/10 border border-[#FC82ED]/30 text-sm space-y-0.5" data-testid="val-confronto">
      {mq > 0 ? <div>€/mq valutazione: <b className="mono" data-testid="val-eur-mq">{fmt(min / mq, 0)} – {fmt(max / mq, 0)}</b></div> : <div className="text-[var(--muted)]">Inserisci i mq per il calcolo €/mq.</div>}
      {pMin != null && <div data-testid="val-diff-min">{sgn(pMin)} sul minimo OMI</div>}
      {pMax != null && <div data-testid="val-diff-max">{sgn(pMax)} sul massimo OMI</div>}
    </div>
  );
}

export function PrezzoPubblicazione({ id, consigliato, prezzo, onChange }) {
  const [val, setVal] = useState("");
  const [busy, setBusy] = useState(false);
  const [r, setR] = useState({ tipologia: "", contratto: "vendita", prezzo_proprietario: "", motivazione: "", comparabili: ["", ""] });
  const [allegati, setAllegati] = useState([]);
  useEffect(() => { if (consigliato) setVal(fmt(consigliato, 0)); }, [consigliato]);
  if (prezzo?.approvato) return <p className="text-xs text-emerald-700" data-testid="val-prezzo-approvato">Prezzo di pubblicazione approvato: € {fmt(prezzo.prezzo, 0)}</p>;
  const comp = r.comparabili.filter((c) => c.trim()).length;
  const ok = parseEur(val) > 0 && parseEur(r.prezzo_proprietario) > 0 && r.tipologia && r.motivazione.trim().length >= 150 && (comp >= 2 || allegati.length > 0);
  const invia = async () => {
    setBusy(true);
    try { await api.post(`/immobili/${id}/richiedi-approvazione-prezzo`, { ...r, prezzo: parseEur(val), prezzo_proprietario: parseEur(r.prezzo_proprietario), allegati: allegati.map((a) => a.id) }); toast.success("Prezzo inviato per l'approvazione"); onChange?.(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const sel = "w-full px-3 py-2.5 rounded-xl border border-[var(--border)] bg-white text-sm";
  return (
    <div className="pt-3 border-t border-[var(--border)] space-y-2" data-testid="val-prezzo-pubblicazione">
      <div className="grid sm:grid-cols-2 gap-2">
        <label className="text-xs font-semibold text-[var(--muted)]">Tipologia *<select data-testid="val-tipologia" value={r.tipologia} onChange={(e) => setR({ ...r, tipologia: e.target.value })} className={`${sel} mt-1`}><option value="">—</option>{TIPOLOGIE.map((t) => <option key={t}>{t}</option>)}</select></label>
        <label className="text-xs font-semibold text-[var(--muted)]">Contratto *<select data-testid="val-contratto" value={r.contratto} onChange={(e) => setR({ ...r, contratto: e.target.value })} className={`${sel} mt-1`}><option value="vendita">Vendita</option><option value="affitto">Affitto</option></select></label>
        <Campo label="Prezzo chiesto dal proprietario *" value={r.prezzo_proprietario} onChange={(v) => setR({ ...r, prezzo_proprietario: v })} testid="val-prezzo-proprietario" />
        <Campo label="Prezzo che proponi di pubblicare *" value={val} onChange={setVal} testid="val-prezzo-pub" />
      </div>
      <div><div className="text-xs font-semibold text-[var(--muted)] mb-1">Valutazione / comparabili (almeno 2 righe oppure un allegato)</div>
        {r.comparabili.map((c, i) => <input key={i} data-testid={`val-comparabile-${i}`} value={c} onChange={(e) => setR({ ...r, comparabili: r.comparabili.map((x, k) => (k === i ? e.target.value : x)) })} placeholder="Es. Via Roma 12, 3 locali, 80 mq, venduto 145.000 €" className={`${sel} mb-1`} />)}
        <button type="button" onClick={() => setR({ ...r, comparabili: [...r.comparabili, ""] })} className="text-xs font-semibold text-[#B8369F]">+ riga</button></div>
      <Allegati value={allegati} onChange={setAllegati} testid="val-allegati" />
      <Motivazione value={r.motivazione} onChange={(v) => setR({ ...r, motivazione: v })} testid="val-motivazione" />
      <button data-testid="val-invia-direttore" onClick={invia} disabled={busy || !ok}
        className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-[#111111] text-sm font-semibold disabled:opacity-40">
        {busy ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Invia
      </button>
      {prezzo?.da_approvare > 0 && <p className="text-xs text-amber-700" data-testid="val-prezzo-in-attesa">In attesa di approvazione: € {fmt(prezzo.da_approvare, 0)}</p>}
    </div>
  );
}

function Storico({ list }) {
  if (!list.length) return null;
  return (
    <div className="pt-3 border-t border-[var(--border)]" data-testid="val-storico">
      <div className="text-[11px] font-bold uppercase tracking-wide text-[var(--muted)] mb-1">Valutazioni salvate</div>
      {list.map((x, i) => (
        <div key={x.id} data-testid={`val-storico-${i}`} className={`py-1.5 text-sm ${i ? "text-slate-500" : ""}`}>
          <div className="flex flex-wrap gap-x-2">
            <b className="mono">€ {fmt(x.min, 0)} – {fmt(x.max, 0)}</b>{x.consigliato && <span>· consigliato € {fmt(x.consigliato, 0)}</span>}
            {i === 0 && <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">valida</span>}
          </div>
          <div className="text-[11px] text-[var(--muted)]">{new Date(x.at).toLocaleString("it-IT")} · {x.by}{x.nota ? ` · ${x.nota}` : ""}</div>
        </div>
      ))}
    </div>
  );
}

export default function ValutazioneImmobilart({ tipo, id, mq, omi, prezzo, onChange }) {
  const [f, setF] = useState({ min: "", max: "", consigliato: "", nota: "" });
  const [list, setList] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = () => api.get(`/omi/valutazioni/${tipo}/${id}`).then((r) => {
    setList(r.data);
    const u = r.data[0];
    if (u) setF({ min: fmt(u.min, 0), max: fmt(u.max, 0), consigliato: u.consigliato ? fmt(u.consigliato, 0) : "", nota: u.nota || "" });
  }).catch(() => {});
  useEffect(() => { load(); }, [tipo, id]);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  const salva = async () => {
    setBusy(true);
    try {
      await api.post(`/omi/valutazione/${tipo}/${id}`, { min: parseEur(f.min), max: parseEur(f.max), consigliato: parseEur(f.consigliato), nota: f.nota, mq, omi });
      toast.success("Valutazione salvata nello storico"); load(); onChange?.();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const ultima = list[0];
  return (
    <div className="mt-4 pt-4 border-t border-[var(--border)] space-y-3" data-testid="valutazione-immobilart">
      <h3 className="font-display font-bold text-sm">Valutazione Immobilart</h3>
      <div className="grid sm:grid-cols-3 gap-2">
        <Campo label="Valore minimo €" value={f.min} onChange={set("min")} testid="val-min" />
        <Campo label="Valore massimo €" value={f.max} onChange={set("max")} testid="val-max" />
        <Campo label="Prezzo consigliato €" value={f.consigliato} onChange={set("consigliato")} testid="val-consigliato" />
      </div>
      <textarea data-testid="val-nota" value={f.nota} onChange={(e) => set("nota")(e.target.value)} rows={2} placeholder="Nota/motivazione (es. ristrutturato 2022, terrazzo 40 mq)"
        className="w-full px-3 py-2.5 rounded-xl border border-[var(--border)] text-base sm:text-sm bg-white" />
      <Confronto min={parseEur(f.min)} max={parseEur(f.max)} mq={Number(mq) || 0} omi={omi} />
      <button data-testid="val-salva" onClick={salva} disabled={busy}
        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#FC82ED] text-white text-sm font-semibold disabled:opacity-50">
        {busy ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Salva valutazione
      </button>
      {tipo === "immobile" && <PrezzoPubblicazione id={id} consigliato={ultima?.consigliato} prezzo={prezzo} onChange={onChange} />}
      <Storico list={list} />
    </div>
  );
}
